import { useContext } from 'react'

import { useForm, useLatestEntity, useThunkDispatch } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import PageContext from '@contexts/pageContext'

import useSelector from '@hooks/useSelector'

import defaultState, { requiredFields } from '@models/webhook'
import type { WebhookModel } from '@models/types'

import * as webhookActions from '@redux/modules/webhook'
import type { DeleteParams } from '@redux/modules/types'

import type { AppDispatch } from '@redux/store'

const stringifyObjectKeys: (keyof WebhookModel)[] = ['enabled_events']

type CreateWebhookParams = {
  dispatch: AppDispatch,
  webhookParams: Partial<WebhookModel>,
}

const createWebhook = (params: CreateWebhookParams) => {
  const {
    dispatch,
    webhookParams,
    webhookParams: { enabled_events },
  } = params
  const { createWebhook: createFn } = webhookActions

  const updatedParams = {
    ...webhookParams,
    enabled_events: JSON.stringify(enabled_events),
  }

  return dispatch(createFn(updatedParams))
}

type UpdateWebhookParams = {
  dispatch: AppDispatch,
  webhook: WebhookModel,
  webhookParams: Partial<WebhookModel>,
}

const updateWebhook = (params: UpdateWebhookParams) => {
  const { webhook, webhookParams, dispatch } = params
  const { updateWebhook: updateFn } = webhookActions

  const updatedParams = mergeAndStringifyEntityObjects(webhook, webhookParams, stringifyObjectKeys)

  return dispatch(updateFn(updatedParams))
}

type DeleteWebhookParams = {
  webhook: DeleteParams<WebhookModel>,
  dispatch: AppDispatch,
}

const deleteWebhook = (params: DeleteWebhookParams) => {
  const { dispatch, webhook } = params
  const { deleteWebhook: deleteFn } = webhookActions

  return dispatch(deleteFn(webhook))
}

export type ShowCreateOrEditWebhookModalPayload = {
  webhook: Partial<WebhookModel>,
}

type CreateOrEditWebhookModalParams = {
  webhook: WebhookModel,
  showCreateOrEditWebhookModal: (payload: ShowCreateOrEditWebhookModalPayload) => void,
}

const createOrEditWebhook = (params: CreateOrEditWebhookModalParams) => new Promise((resolve, reject) => {
  const { webhook, showCreateOrEditWebhookModal } = params

  if (showCreateOrEditWebhookModal){
    const payload = {
      webhook,
    }

    showCreateOrEditWebhookModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateOrEditWebhookModal not defined in PageContext callbacks'))
})

export function useWebhookForm(
  webhook: Partial<WebhookModel>,
  options: UseFormOptions = {},
){
  const { validateOn } = options

  const webhookForm = useForm(
    defaultState,
    { entity: webhook, requiredFields, validateOn },
    [webhook.id, webhook.cache_key],
  )

  return {
    ...webhookForm,
  }
}

function useWebhook(initEntity: Partial<WebhookModel> = {}){

  // @ts-expect-error TS2322
  const { entity: webhook }: { entity: WebhookModel } = useLatestEntity(initEntity, 'webhooks')

  const dispatch = useThunkDispatch()

  const { callbacks } = useContext(PageContext)
  const { showCreateOrEditWebhookModal } = callbacks || {}

  const {
    creating, deleting, loading, updating,
  } = useSelector(reduxState => reduxState.webhooks)

  return {
    callbacks: {
      createOrEditWebhook: () => createOrEditWebhook({ webhook, showCreateOrEditWebhookModal }),
      createWebhook: (webhookParams: Partial<WebhookModel>) => createWebhook({ webhookParams, dispatch }),
      deleteWebhook: () => deleteWebhook({ webhook, dispatch }),
      updateWebhook: (webhookParams: Partial<WebhookModel>) => updateWebhook({ webhook, webhookParams, dispatch }),
    },
    creating,
    deleting,
    loading,
    updating,
    webhook,
  }
}

export default useWebhook
